import { Bell, Bot, Hash, Mail, MessageCircle, Smartphone } from "lucide-react";

import type { ChannelType } from "../../../notifications-admin-api";

/**
 * The notification-type/channel catalogue shared between `./index.tsx` (the
 * matrix itself) and `./bulk-edit-modal.tsx` (the secondary header's "Edit"
 * button's bulk-edit modal) — pulled out to its own module specifically so
 * neither of those two files has to import FROM the other to reach it.
 * `index.tsx` used to export these directly and `bulk-edit-modal.tsx`
 * imported them from `"./index"`, which is a real circular import (A imports
 * B imports A) — harmless in this particular case only because every use
 * is deferred to inside a render function, never read at module-evaluation
 * time, but that's a fragile thing to rely on rather than a reason to keep
 * it. This module has no dependency on either file, so the cycle is gone
 * outright instead of just tolerated.
 */

export const CHANNEL_LABEL: Record<ChannelType, string> = {
  email: "Email",
  whatsapp: "WhatsApp",
  slack: "Slack",
  push: "Push",
  in_app: "In-app",
  mcp_event: "MCP",
};

export const CHANNEL_ICON: Record<ChannelType, typeof Mail> = {
  email: Mail,
  whatsapp: MessageCircle,
  slack: Hash,
  push: Smartphone,
  in_app: Bell,
  mcp_event: Bot,
};

/**
 * The real, complete `notification.preference.notification_type` catalogue
 * — NOT the BFF's own `/notifications/admin/notification-types` route,
 * which only lists 6 of the 10 real types (missing all 3
 * `scheduled_email.*` sentiment/agenda types entirely). Traced from the
 * actual user-facing settings page in the core app
 * (`app/web/app/screens/settings/components/qa-run-preferences.ts` +
 * `portfolio-update-preferences.ts`) and the BFF's own type literals
 * (`services/sentiments/notification-type.ts`,
 * `services/reporting/lib/{scheduled-alerts,subscriptions}.ts`) — that
 * codebase's own `TECH DEBT` comment notes these are hardcoded in three
 * places (FE config, BFF admin registry, dispatch) pending a real DB-backed
 * catalogue; this is a fourth, deliberately kept in lockstep with the FE's
 * two config files rather than the narrower admin route. `report_subscription`
 * (per-report, `reference_id`-scoped) is deliberately excluded — it isn't a
 * single fixed setting the way every type below is.
 *
 * Each type only lists the channels it can actually be set on (e.g. the
 * `scheduled_email.*` sentiment/agenda types are email-only) — a cell badge
 * for a channel a type doesn't support isn't just empty, it's meaningless,
 * so it doesn't render at all rather than showing disabled.
 */
export const NOTIFICATION_TYPE_GROUPS: {
  group: string;
  types: { type: string; label: string; channels: ChannelType[] }[];
}[] = [
  {
    group: "Reports",
    types: [
      { type: "report_new_rows", label: "Report new rows", channels: ["email", "whatsapp", "mcp_event"] },
      { type: "report_subscribed", label: "Report subscribed", channels: ["email", "whatsapp", "mcp_event"] },
      { type: "report_test", label: "Report test", channels: ["email"] },
    ],
  },
  {
    group: "Company updates",
    types: [
      {
        type: "COMPANY_UPDATE_MATCHED",
        label: "Company update matched",
        channels: ["email", "in_app", "mcp_event"],
      },
      {
        type: "COMPANY_UPDATE_REVIEW_REQUIRED",
        label: "Company update needs review",
        channels: ["email", "in_app", "mcp_event"],
      },
      {
        type: "COMPANY_UPDATE_PROCESSING_FAILED",
        label: "Company update processing failed",
        channels: ["email", "in_app", "mcp_event"],
      },
    ],
  },
  {
    group: "QA",
    types: [
      {
        type: "qa_run_scheduled",
        label: "Scheduled runs",
        channels: ["email", "in_app", "whatsapp", "slack", "push", "mcp_event"],
      },
      { type: "qa_run_mine", label: "My runs", channels: ["email", "in_app", "whatsapp", "push", "mcp_event"] },
      {
        type: "qa_run_others",
        label: "Other users' runs",
        channels: ["email", "in_app", "whatsapp", "push", "mcp_event"],
      },
    ],
  },
  {
    group: "Scheduled emails",
    types: [
      {
        type: "scheduled_email.weekly-sentiment-summary",
        label: "Weekly sentiment summary",
        channels: ["email", "mcp_event"],
      },
      {
        type: "scheduled_email.sentiment-form-chaser",
        label: "Sentiment form chaser",
        channels: ["email", "mcp_event"],
      },
      {
        type: "scheduled_email.agenda-reminder",
        label: "Agenda reminder",
        channels: ["email", "mcp_event"],
      },
    ],
  },
];

/** Every type across every group, flattened, each carrying its own index —
 * used to alternate column shading per type, consistently between the
 * header and body rows. */
export const FLAT_TYPES = NOTIFICATION_TYPE_GROUPS.flatMap((group, groupIndex) =>
  group.types.map((type, indexInGroup) => ({
    ...type,
    group: group.group,
    groupIndex,
    isGroupStart: indexInGroup === 0,
  })),
).map((type, typeIndex) => ({ ...type, typeIndex }));

/**
 * Whether a spacer column belongs immediately BEFORE this type's column —
 * i.e. this type starts a new group, and it isn't the very first group
 * (excludes the outer edges: no spacer before the first group's first
 * column, none after the last group's last column, one between every pair
 * of adjacent groups). The spacer is what's allowed to grow (see
 * `.spacer`'s `width: auto` vs every real column's `width: 1%` in
 * `index.module.css` — the standard `table-layout: auto` trick for "these
 * columns shrink to their content, this other one absorbs the rest").
 */
export function needsSpacerBefore(type: { groupIndex: number; isGroupStart: boolean }): boolean {
  return type.isGroupStart && type.groupIndex > 0;
}
